import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';

interface ForgeSidebarProps {
  theme: 'dark' | 'light';
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  toggleCollapse: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
}

interface NavSection {
  title: string;
  items: { label: string; icon: React.ReactNode; path: string }[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Navigation',
    items: [
      { label: 'Home', icon: <HomeOutlinedIcon />, path: '/forge' },
      { label: 'Task Player', icon: <PlayArrowOutlinedIcon />, path: '/forge/tasks/player' },
    ],
  },
  {
    title: 'Dashboards',
    items: [
      { label: 'Live Status', icon: <MonitorHeartOutlinedIcon />, path: '/forge/dashboards/live-status' },
      { label: 'Daily Report', icon: <CalendarTodayOutlinedIcon />, path: '/forge/dashboards/daily-report' },
      { label: 'Aggregated', icon: <TimelineOutlinedIcon />, path: '/forge/dashboards/aggregated' },
      { label: 'Labeling Admin', icon: <ManageSearchOutlinedIcon />, path: '/forge/dashboards/labeling' },
    ],
  },
  {
    title: 'Admin',
    items: [
      { label: 'Queue Management', icon: <DashboardOutlinedIcon />, path: '/forge/admin/queues' },
      { label: 'Labeler Management', icon: <PeopleOutlinedIcon />, path: '/forge/admin/labelers' },
      { label: 'Chain Config', icon: <LinkOutlinedIcon />, path: '/forge/admin/chains' },
      { label: 'QA Review', icon: <RuleOutlinedIcon />, path: '/forge/admin/qa-review' },
      { label: 'Master Label', icon: <ViewColumnOutlinedIcon />, path: '/forge/admin/master-label' },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Goals', icon: <FlagOutlinedIcon />, path: '/forge/system/goals' },
      { label: 'Task List', icon: <DescriptionOutlinedIcon />, path: '/forge/system/tasks' },
      { label: 'Flow Diagrams', icon: <AccountTreeOutlinedIcon />, path: '/forge/system/flows' },
      { label: 'Database', icon: <StorageOutlinedIcon />, path: '/forge/system/database' },
    ],
  },
];

export const ForgeSidebar: React.FC<ForgeSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  toggleCollapse,
  onDragStart,
  toggleTheme,
  currentPath,
}) => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    if (path === '/forge') return currentPath === '/forge' || currentPath === '/forge/';
    return currentPath.startsWith(path);
  };

  const sidebarClasses = [
    'forge-sidebar',
    isCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={sidebarClasses} style={{ width: isCollapsed ? undefined : width }}>
      {/* Nav */}
      <nav className="sidebar-nav">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} style={{ marginBottom: 16 }}>
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                title={isCollapsed ? item.label : undefined}
              >
                {item.icon}
                <span className="sidebar-item-label">{item.label}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          <button className="sidebar-footer-link" title={isCollapsed ? 'Admin' : undefined}>
            <AdminPanelSettingsOutlinedIcon />
            <span className="sidebar-collapse-label">Admin</span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={toggleTheme}
            title={isCollapsed ? `Switch to ${theme === 'dark' ? 'light' : 'dark'} mode` : undefined}
          >
            {theme === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            <span className="sidebar-collapse-label">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => navigate('/forge/profile')}
            title={isCollapsed ? 'Profile' : undefined}
          >
            <PersonOutlinedIcon />
            <span className="sidebar-collapse-label">Profile</span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => { logout(); navigate('/stores'); }}
            title={isCollapsed ? 'Sign out' : undefined}
          >
            <LogoutIcon />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>

        <div className="sidebar-footer-divider" />

        <button className="sidebar-collapse-btn" onClick={toggleCollapse} title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          <span className="sidebar-collapse-label">Collapse</span>
        </button>
      </div>

      <div className="sidebar-drag-handle" onMouseDown={onDragStart} />
    </aside>
  );
};
